import { Injectable } from '@angular/core';
import {AbstractDatabaseService} from "../../tandem-core/services/abstract-database.service";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {TandemUser} from "../../auth/models/tandem-user";
import {Observable, of, switchMap} from "rxjs";
import {Timestamp} from "firebase/firestore";
import {CashFlowTemplate} from "../models/cash-flow-template";
import {CashFlow} from "../models/cash-flow";

@Injectable({
  providedIn: 'root'
})
export class CashFlowTemplateService extends AbstractDatabaseService<CashFlowTemplate> {

  private DEFAULT_TEMPLATE: CashFlowTemplate = {
    revenueSections: [
      {
        name: 'Revenue',
        description: 'Cash coming in',
        hoverHint: 'An estimation of all potential income sources.',
        categories: [
          {
            name: 'Active Income',
            items: [],
            total: 0,
            hoverHint: 'Income earned from regular work or business activities.'
          },
          {
            name: 'Passive Income',
            items: [],
            total: 0,
            hoverHint: 'Income earned without active daily effort, e.g. from investments.'
          },
          {
            name: 'Unexpected Income',
            items: [],
            total: 0,
            hoverHint: 'Income received that wasn\'t anticipated or regularly expected.'
          },
          {
            name: 'Asset Liquidation',
            items: [],
            total: 0,
            hoverHint: 'Income from selling assets like stocks, property, or other valuables.'
          }
        ]
      }
    ],
    expenseSections: [
      {
        name: 'Pay Yourself First Expenses',
        description: 'Expenses that pay into the future of the most important person in your life, you',
        hoverHint: 'Expenses that cater to personal financial growth and security.',
        categories: [
          {
            name: 'Charitable Contributions',
            items: [],
            total: 0,
            hoverHint: 'Income freely given to charitable or faith-inspired causes.'
          },
          {
            name: 'Investing In Yourself',
            items: [],
            total: 0,
            hoverHint: 'Expenses for self-improvement, education, or personal projects.'
          },
          {
            name: 'Working Assets',
            items: [],
            total: 0,
            hoverHint: 'Investments into assets that generate income or appreciate over time.'
          },
          {
            name: 'Debt Paydown',
            items: [],
            total: 0,
            hoverHint: 'Allocations specifically for reducing outstanding debts.'
          }
        ]
      },
      {
        name: 'Essential Expenses',
        description: 'Expenses essential to your basic survival; groceries, housing, transportation, etc',
        hoverHint: 'Necessary costs without which daily life would be significantly affected.',
        categories: [
          {
            name: 'Housing',
            items: [],
            total: 0,
            hoverHint: 'Expenses related to rent, mortgage, and other housing-related costs.'
          },
          {
            name: 'Transportation',
            items: [],
            total: 0,
            hoverHint: 'Costs associated with owning, maintaining, and using vehicles or public transport.'
          },
          {
            name: 'Insurance & Utilities',
            items: [],
            total: 0,
            hoverHint: 'Payments for protective policies and essential utilities like water, electricity, etc.'
          },
          {
            name: 'Groceries & Toiletries',
            items: [],
            total: 0,
            hoverHint: 'Expenses for food, household items, and personal care products.'
          },
          {
            name: 'Medical',
            items: [],
            total: 0,
            hoverHint: 'Health-related expenses including doctor visits, medicines, treatments, etc.'
          },
          {
            name: 'Childcare',
            items: [],
            total: 0,
            hoverHint: 'Expenses for daycare, babysitting, schooling, and other child-related costs.'
          },
          {
            name: 'Debt Payments',
            items: [],
            total: 0,
            hoverHint: 'Regular payments towards loans and credit card debts.'
          },
          {
            name: 'Other',
            items: [],
            total: 0,
            hoverHint: 'Any essential expense that doesn\'t fit into the above categories.'
          }
        ]
      },
      {
        name: 'Lifestyle Expenses',
        description: 'Expenses that are intended to improve your quality of life',
        hoverHint: 'Non-essential costs which vary based on personal choices and lifestyle.',
        categories: [
          {
            name: 'Self Care',
            items: [],
            total: 0,
            hoverHint: 'Expenses for personal pampering, relaxation, and wellness.'
          },
          {
            name: 'Going Out',
            items: [],
            total: 0,
            hoverHint: 'Costs for recreational activities, dining out, movies, events, etc.'
          },
          {
            name: 'Shopping',
            items: [],
            total: 0,
            hoverHint: 'Expenditures on clothes, gadgets, and other shopping sprees.'
          },
          {
            name: 'Travel & Vacation',
            items: [],
            total: 0,
            hoverHint: 'Expenses for trips, getaways, flights, accommodation, etc.'
          },
          {
            name: 'Subscriptions',
            items: [],
            total: 0,
            hoverHint: 'Recurring costs for services like magazines, streaming platforms, gym memberships, etc.'
          },
          {
            name: 'Other',
            items: [],
            total: 0,
            hoverHint: 'Any discretionary expense that doesn\'t fit into the above categories.'
          }
        ]
      }
    ],
    coachId: '',
    userId: '',
    dateCreated: Timestamp.now(),
    dateModified: Timestamp.now()
  };
  constructor(protected override afs: AngularFirestore) {
    super('cashFlowTemplates', afs);
  }

  public getTemplateForUser(user: TandemUser): Observable<CashFlowTemplate> {
    if (user.cashFlowTemplateId) {
      return this.get(user.cashFlowTemplateId);
    } else if (user.coachId) {
      return this.getTemplateByCoachId(user.coachId);
    } else {
      return this.getDefaultTemplate();
    }
  }

  public generateDefaultCashFlow(): CashFlow {
    let startDate = new Date();
    let endDate = new Date(startDate); // Clone startDate
    endDate.setMonth(startDate.getMonth()); // Add 1 month to endDate
    return {
      dateCreated: Timestamp.now(),
      dateModified: Timestamp.now(),
      title: `Cash Flow from ${startDate.toDateString()} to ${endDate.toDateString()}`,
      startDate: Timestamp.fromDate(startDate),
      endDate: Timestamp.fromDate(endDate),
      userId: '',
      netCashFlow: 0,
      revenueSections: this.DEFAULT_TEMPLATE.revenueSections,
      expenseSections: this.DEFAULT_TEMPLATE.expenseSections,
      totalRevenue: 0,
      totalExpenses: 0,
      visibleToCoach: true
    }
  }

  public generateDefaultTemplate(userId: string): CashFlowTemplate {
    let template = this.DEFAULT_TEMPLATE;
    template.userId = userId;
    return template;
  }

  private getTemplateByCoachId(coachId: string): Observable<CashFlowTemplate> {
    return this.afs.collection<CashFlowTemplate>('cashFlowTemplates', ref => ref.where('coachId', '==', coachId)).valueChanges().pipe(
      switchMap(templates => {
        if (templates.length > 0) {
          return of(templates[0]);
        } else {
          return this.getDefaultTemplate();
        }
      })
    );
  }

  private getDefaultTemplate(): Observable<CashFlowTemplate> {
    return of(this.DEFAULT_TEMPLATE);
  }
}
